'use client';

import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import type { Locale } from '../../i18n/locales';
import type { SiteSettingsData } from '../../types/content';
import { getSupabaseBrowserClientSafe } from '../../lib/supabase/client';

type SiteSettingsContextType = {
    settings: SiteSettingsData;
};

type SiteSettingsRow = {
    title?: string;
    body?: string;
    primary_cta_label?: string;
    primary_cta_href?: string;
    header_cta_label?: string;
    header_cta_href?: string;
    header_secondary_label?: string;
    header_secondary_href?: string;
    footer_brand?: string;
    footer_copyright?: string;
};

type SiteNavigationRow = {
    label?: string;
    href?: string;
};

type SiteNavigationChildRow = {
    parent_href?: string;
    label?: string;
    href?: string;
};

type SiteFooterRow = {
    group_label?: string;
    label?: string;
    href?: string;
};

type SiteSocialRow = {
    label?: string;
    href?: string;
};

const SiteSettingsContext = createContext<SiteSettingsContextType | undefined>(undefined);

export function RealtimeSiteSettingsProvider({
    initialSettings,
    locale,
    children,
}: {
    initialSettings: SiteSettingsData;
    locale: Locale;
    children: React.ReactNode;
}) {
    const [settings, setSettings] = useState<SiteSettingsData>(initialSettings);
    const supabase = useMemo(() => getSupabaseBrowserClientSafe(), []);

    const refreshSettings = useCallback(async () => {
        if (!supabase) return;

        const [siteRow, navRows, navChildRows, footerRows, socialRows] = await Promise.all([
            supabase
                .from('site_settings')
                .select('*')
                .eq('locale', locale)
                .order('updated_at', { ascending: false })
                .limit(1)
                .maybeSingle(),
            supabase.from('site_navigation').select('*').eq('locale', locale).order('order', { ascending: true }),
            supabase.from('site_navigation_children').select('*').eq('locale', locale).order('order', { ascending: true }),
            supabase.from('site_footer_links').select('*').eq('locale', locale).order('order', { ascending: true }),
            supabase.from('site_social_links').select('*').eq('locale', locale).order('order', { ascending: true }),
        ]);

        const childrenByParent = ((navChildRows.data || []) as SiteNavigationChildRow[]).reduce<Record<string, { label: string; href: string }[]>>(
            (acc, row) => {
                const parentHref = row.parent_href || '';
                if (!parentHref) return acc;
                if (!acc[parentHref]) acc[parentHref] = [];
                acc[parentHref].push({
                    label: row.label || '',
                    href: row.href || '',
                });
                return acc;
            },
            {},
        );

        setSettings({
            siteTitle: (siteRow.data as SiteSettingsRow | null)?.title || initialSettings.siteTitle,
            body: (siteRow.data as SiteSettingsRow | null)?.body || initialSettings.body,
            primaryCtaLabel: (siteRow.data as SiteSettingsRow | null)?.primary_cta_label || initialSettings.primaryCtaLabel,
            primaryCtaHref: (siteRow.data as SiteSettingsRow | null)?.primary_cta_href || initialSettings.primaryCtaHref || '/autovakuutus',
            headerCtaLabel: (siteRow.data as SiteSettingsRow | null)?.header_cta_label || initialSettings.headerCtaLabel,
            headerCtaHref: (siteRow.data as SiteSettingsRow | null)?.header_cta_href || initialSettings.headerCtaHref,
            headerSecondaryLabel: (siteRow.data as SiteSettingsRow | null)?.header_secondary_label || initialSettings.headerSecondaryLabel,
            headerSecondaryHref: (siteRow.data as SiteSettingsRow | null)?.header_secondary_href || initialSettings.headerSecondaryHref,
            footerBrand: (siteRow.data as SiteSettingsRow | null)?.footer_brand || initialSettings.footerBrand,
            footerCopyright: (siteRow.data as SiteSettingsRow | null)?.footer_copyright || initialSettings.footerCopyright,
            navigation:
                navRows.data?.length
                    ? (navRows.data as SiteNavigationRow[])
                        .map((row) => ({
                            label: row.label || '',
                            href: row.href || '',
                            children: (childrenByParent[row.href || ''] || []).filter(
                                (child) => child.label && child.href,
                            ),
                        }))
                        .filter((row) => row.label && row.href)
                    : initialSettings.navigation,
            footerLinks:
                footerRows.data?.length
                    ? (footerRows.data as SiteFooterRow[])
                        .map((row) => ({
                            groupLabel: row.group_label || undefined,
                            label: row.label || '',
                            href: row.href || '',
                        }))
                        .filter((row) => row.label && row.href)
                    : initialSettings.footerLinks,
            socialLinks:
                socialRows.data?.length
                    ? (socialRows.data as SiteSocialRow[])
                        .map((row) => ({
                            label: row.label || '',
                            href: row.href || '',
                        }))
                        .filter((row) => row.label && row.href)
                    : initialSettings.socialLinks,
        });
    }, [supabase, locale, initialSettings]);

    useEffect(() => {
        setSettings(initialSettings);
    }, [initialSettings]);

    useEffect(() => {
        if (!supabase) return;

        const channel = supabase
            .channel('site-settings-live')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings', filter: `locale=eq.${locale}` }, refreshSettings)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'site_navigation', filter: `locale=eq.${locale}` }, refreshSettings)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'site_navigation_children', filter: `locale=eq.${locale}` }, refreshSettings)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'site_footer_links', filter: `locale=eq.${locale}` }, refreshSettings)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'site_social_links', filter: `locale=eq.${locale}` }, refreshSettings)
            .subscribe();

        void refreshSettings();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [supabase, locale, refreshSettings]);

    return (
        <SiteSettingsContext.Provider value={{ settings }}>
            {children}
        </SiteSettingsContext.Provider>
    );
}

export function useSiteSettings() {
    const context = useContext(SiteSettingsContext);
    if (!context) {
        throw new Error('useSiteSettings must be used within RealtimeSiteSettingsProvider');
    }
    return context.settings;
}
